import type { ReactNode } from 'react';
import { useNavigate, type To } from 'react-router-dom';
import { Button, type ButtonProps } from '@/components/ui';

interface GuardedLinkProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** Navigationsziel (react-router-`To`). */
  to: To;
  /** Wenn `true`, navigiert der Klick nicht — stattdessen wird `onBlocked` gerufen. */
  isDirty: boolean;
  /**
   * Wird beim geblockten Klick statt `navigate(to)` aufgerufen.
   * Caller-Pflicht: Confirm-Dialog oeffnen und nach User-Entscheidung
   * selbst `navigate(to)` ausloesen oder abbrechen.
   */
  onBlocked: (to: To) => void;
  children: ReactNode;
}

/**
 * Spec 0216 §5.2 (Plan §0.2 Option B): Drop-in-Replacement fuer einen
 * Navigations-Button in der Routing-View. Wenn ungespeicherte Aenderungen
 * existieren, wird die Navigation abgefangen — der Caller bekommt das
 * Ziel via `onBlocked` und kann ein App-Modal oeffnen (siehe
 * `discard-confirmation.tsx`).
 *
 * Begrenzung (siehe Plan §5.2c): Header-Nav-Links, Browser-Back-Button und
 * manuelle URL-Aenderungen bleiben ungesichert — dafuer greift nur
 * `useUnsavedChangesPrompt` (Browser-eigener `beforeunload`-Dialog).
 */
export function GuardedLink({
  to,
  isDirty,
  onBlocked,
  children,
  ...buttonProps
}: GuardedLinkProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (isDirty) {
      onBlocked(to);
      return;
    }
    navigate(to);
  };

  return (
    <Button onClick={handleClick} {...buttonProps}>
      {children}
    </Button>
  );
}
